/**
 * Binds the connector target transaction to the live UI stores and to the
 * parameter mutation it drives. The transaction itself stays free of both.
 */

import { invalidateFamilySnapshotCache } from '../../revit/familySnapshotCache';
import { useConfigurationStore } from '../../../stores/useConfigurationStore';
import { useUIStore } from '../../../stores/useUIStore';
import type {
  ConnectorApplyOutcome,
  ConnectorDocumentIdentity,
  ConnectorTargetPorts,
  ConnectorTargetSelection,
} from '../../connectorTarget';
import type { ParameterAdditionOptions, ParameterAdditionResult } from './models';
import type { ConnectorParameterTarget } from '../../../types/config/connector.types';

export interface ConnectorTargetPortContext {
  readIdentity: () => ConnectorDocumentIdentity | null;
  addParameters: (options?: ParameterAdditionOptions) => Promise<ParameterAdditionResult>;
  undoLastConnectorParameterAddition: () => Promise<boolean>;
  /** Passed straight through to the mutation the caller asked for. */
  options?: ParameterAdditionOptions;
  /** Hands the rich mutation result back, which the plain port result drops. */
  onApplyResult: (result: ParameterAdditionResult) => void;
}

/** The target the radios currently show, which can differ from the frozen one. */
export const readLiveConnectorTarget = (): ConnectorParameterTarget => {
  const { parameterDestination, parameterBindingKind } =
    useConfigurationStore.getState().connectorBindingConfiguration;

  return { destination: parameterDestination, bindingKind: parameterBindingKind };
};

const applyFrozenTarget = async (
  context: ConnectorTargetPortContext,
  target: ConnectorTargetSelection
): Promise<ConnectorApplyOutcome> => {
  const result = await context.addParameters({
    ...(context.options ?? {}),
    connectorTarget: {
      destination: target.destination,
      bindingKind: target.bindingKind,
    },
  });
  context.onApplyResult(result);

  return { success: result.success, ...(result.error ? { error: result.error } : {}) };
};

/**
 * Stamps provenance onto whatever checkpoint the mutation just recorded. A
 * mutation that recorded none leaves nothing to stamp.
 */
const stampAppliedTarget: ConnectorTargetPorts['stampAppliedTarget'] = (applied) => {
  const { connectorParameterUndoPlan, setConnectorParameterUndoPlan } = useUIStore.getState();
  if (!connectorParameterUndoPlan) {
    return;
  }

  // A new object, because the store drops an update that keeps plan identity.
  setConnectorParameterUndoPlan({ ...connectorParameterUndoPlan, appliedTarget: applied });
};

export const createConnectorTargetPorts = (
  context: ConnectorTargetPortContext
): ConnectorTargetPorts => ({
  readCheckpoint: () => useUIStore.getState().connectorParameterUndoPlan,
  readActiveIdentity: context.readIdentity,
  undoAppliedTarget: () => context.undoLastConnectorParameterAddition(),
  applyTarget: (target) => applyFrozenTarget(context, target),
  stampAppliedTarget,
  invalidateModelCache: () => {
    invalidateFamilySnapshotCache(context.readIdentity()?.projectKey);
  },
});
